import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { anchorPendingToggle, lockScrollHeight } from './scrollAnchor';
import './style.scss';

export type GroupedViewSection = 'activity' | 'sql-statistics';

const PATHS: Record<GroupedViewSection, { individual: string, grouped: string }> = {
    'activity': { individual: 'activity', grouped: 'activity/groupedstatements' },
    'sql-statistics': { individual: 'sql-statistics', grouped: 'sql-statistics/grouped' },
};

const getOptions = (individualLabel: string) => [
    { label: individualLabel, grouped: false, icon: 'fa-list', tooltip: 'Show without grouping' },
    { label: 'Grouped', grouped: true, icon: 'fa-layer-group', tooltip: 'Group items that differ only by literal values' },
];

const storageKey = (section: GroupedViewSection, instanceId: number | string) => `groupedView_${section}_${instanceId}`;

export const isGroupedViewPreferred = (section: GroupedViewSection, instanceId: number | string): boolean => {
    try {
        return localStorage.getItem(storageKey(section, instanceId)) === 'grouped';
    } catch (error) {
        return false;
    }
};

export const getGroupedViewPath = (section: GroupedViewSection, instanceId: number | string, grouped: boolean) =>
        `/instances/${instanceId}/${grouped ? PATHS[section].grouped : PATHS[section].individual}`;

const GroupedViewToggle = (props: { section: GroupedViewSection, instanceId: number | string, isGroup: boolean, individualLabel?: string, tab?: string, className?: string }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const toggleRef = useRef<HTMLDivElement>(null);

    // Restore the toggle's viewport position after switching views (see scrollAnchor.ts). Keyed on
    // the location too, as some routes reuse the same toggle instance instead of remounting it.
    useEffect(() => anchorPendingToggle(toggleRef.current, props.tab), [location.key, props.tab]);

    const onChange = (grouped: boolean) => {
        if (grouped === props.isGroup) return;
        try {
            localStorage.setItem(storageKey(props.section, props.instanceId), grouped ? 'grouped' : 'individual');
        } catch (error) {
            console.warn('Failed to save grouped view selection to localStorage:', error);
        }
        const params = new URLSearchParams(location.search);
        params.delete('page');
        lockScrollHeight(toggleRef.current, props.tab);
        navigate({
            pathname: getGroupedViewPath(props.section, props.instanceId, grouped),
            search: params.toString(),
        }, { state: props.tab ? { groupedViewTab: props.tab } : undefined });
    };

    return (
            <div ref={toggleRef} className={`btn-group grouped-view-toggle float-end${props.className ? ` ${props.className}` : ''}`} role="group" aria-label="Statement grouping">
                {getOptions(props.individualLabel || 'Statements').map(option => {
                    const active = option.grouped === props.isGroup;
                    return (
                            <button key={option.label}
                                    type="button"
                                    className={`btn btn-xsm${active ? ' active' : ''}`}
                                    aria-pressed={active}
                                    data-tooltip-id="global-tooltip"
                                    data-tooltip-content={option.tooltip}
                                    onClick={() => onChange(option.grouped)}>
                                <i className={`fal ${option.icon} fa-fw`} aria-hidden="true"/>
                                {option.label}
                            </button>
                    );
                })}
            </div>
    );
};

export default GroupedViewToggle;
